/**
 * The NAVIGATOR's five boards share one ship, drawn three ways — from the side (FLY THE GAPS),
 * from above (ASTEROID FIELD, JOIN THE STARS) and from the front on its landing legs (DOCK THE
 * SHIP) — and a scatter of points for their skies.
 *
 * The ship is lit from above: a pale hull darkening underneath, a glass canopy with a highlight,
 * a stripe in the era's colour and a thruster whose flame grows with the burn. The flame's length
 * moves; its brightness never does. A hit shows a shield bubble that fades out once — no blinking.
 */
import { ERA_SKIN } from '../../art.js';
import type { Era } from '../../art.js';
import { useSvgId } from '../kit.js';

export interface Hull {
  light: string;
  mid: string;
  dark: string;
  /** The era's stripe and fins. */
  trim: string;
  glass: string;
  /** The thruster's flame. */
  flame: string;
}

const FLAME: Record<Era, string> = {
  stars: '#8fd0ff',
  neon: '#5fe8ff',
  debris: '#ff9a4a',
  dust: '#ffc46b',
  canopy: '#ffe9a6',
};

export function hullOf(era: Era): Hull {
  const skin = ERA_SKIN[era];
  return { light: '#f4f8fb', mid: '#a9b7c4', dark: '#46535f', trim: skin.ink, glass: skin.haze, flame: FLAME[era] };
}

/** The shared gradients of one ship. */
function HullDefs({ id, h }: { id: string; h: Hull }) {
  return (
    <defs>
      <linearGradient id={`${id}h`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={h.light} />
        <stop offset=".55" stopColor={h.mid} />
        <stop offset="1" stopColor={h.dark} />
      </linearGradient>
      <linearGradient id={`${id}v`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={h.dark} />
        <stop offset=".35" stopColor={h.light} />
        <stop offset=".7" stopColor={h.mid} />
        <stop offset="1" stopColor={h.dark} />
      </linearGradient>
      <linearGradient id={`${id}c`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity=".95" />
        <stop offset=".35" stopColor={h.glass} />
        <stop offset="1" stopColor="#0b1a2a" />
      </linearGradient>
      <radialGradient id={`${id}g`}>
        <stop offset="0" stopColor={h.flame} stopOpacity=".7" />
        <stop offset="1" stopColor={h.flame} stopOpacity="0" />
      </radialGradient>
      <linearGradient id={`${id}f`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={h.flame} stopOpacity="0" />
        <stop offset=".55" stopColor={h.flame} stopOpacity=".85" />
        <stop offset="1" stopColor="#ffffff" />
      </linearGradient>
      <linearGradient id={`${id}d`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset=".45" stopColor={h.flame} stopOpacity=".85" />
        <stop offset="1" stopColor={h.flame} stopOpacity="0" />
      </linearGradient>
    </defs>
  );
}

/**
 * The ship from the side, nose to the right, about 46 × 28 around (0, 0).
 * `burn` 0–1 is how hard the thruster pushes; `shield` 0–1 the fading bubble after a hit.
 */
export function ShipSide({ era, burn = 0.3, shield = 0 }: { era: Era; burn?: number; shield?: number }) {
  const id = useSvgId();
  const h = hullOf(era);
  const len = 0.55 + 0.9 * burn;
  return (
    <g>
      <HullDefs id={id} h={h} />
      <ellipse cx="-22" cy="0" rx={9 + 9 * burn} ry={7 + 2 * burn} fill={`url(#${id}g)`} />
      <g transform={`translate(-20 0) scale(${len} 1)`}>
        <path d="M0 -4.2 Q -11 -3 -21 0 Q -11 3 0 4.2 Z" fill={`url(#${id}f)`} />
        <path d="M0 -2 Q -6 -1.3 -10 0 Q -6 1.3 0 2 Z" fill="#ffffff" opacity=".9" />
      </g>
      <path d="M-9 -5 L-16 -13.5 L-21 -13.5 L-16 -4 Z" fill={h.trim} />
      <path d="M-9 -5 L-16 -13.5 L-18 -13.5" stroke="#ffffff" strokeOpacity=".45" strokeWidth=".7" fill="none" />
      <rect x="-22" y="-5" width="5" height="10" rx="1.5" fill={h.dark} />
      <path d="M23 1 C 17 -6, 4 -9.5, -12 -8 L -19 -5 L -19 5 L -12 8 C 4 9, 17 7, 23 1 Z" fill={`url(#${id}h)`} stroke={h.dark} strokeWidth=".8" />
      <path d="M-1 5 L-12 14.5 L-18 14.5 L-11 5 Z" fill={h.trim} />
      <path d="M-14 2 L 17 2" stroke={h.trim} strokeWidth="1.5" opacity=".9" />
      <path d="M6 -4.6 C 10 -7, 15.5 -5.2, 18.5 -1.4 L 6.5 -1.4 Z" fill={`url(#${id}c)`} />
      <path d="M9 -4.9 C 11 -5.8, 13.5 -5.3, 15 -4.1" stroke="#ffffff" strokeWidth=".8" opacity=".85" fill="none" />
      <path d="M-11 -7 C 1 -8.3, 12 -7, 18 -3.4" stroke="#ffffff" strokeWidth=".7" opacity=".5" fill="none" />
      <circle cx="21.2" cy="1" r="1.1" fill={h.flame} />
      {shield > 0 && (
        <ellipse cx="1" cy="0" rx="30" ry="19" fill={h.flame} fillOpacity={0.12 * shield} stroke={h.flame} strokeWidth="1.6" opacity={shield} />
      )}
    </g>
  );
}

/**
 * The ship from above, nose up, about 40 × 46 around (0, 0). `bank` tips it as it slides
 * (degrees), `burn` 0–1 sets the flames.
 */
export function ShipTop({ era, burn = 0.5, bank = 0, shield = 0 }: { era: Era; burn?: number; bank?: number; shield?: number }) {
  const id = useSvgId();
  const h = hullOf(era);
  const len = 0.6 + 0.7 * burn;
  return (
    <g>
      <HullDefs id={id} h={h} />
      <ellipse cx="0" cy="22" rx={12 + 4 * burn} ry={9 + 6 * burn} fill={`url(#${id}g)`} />
      {[-4.5, 4.5].map((x) => (
        <g key={x} transform={`translate(${x} 16) scale(1 ${len})`}>
          <path d="M-2.6 0 Q -2 9 0 17 Q 2 9 2.6 0 Z" fill={`url(#${id}d)`} />
        </g>
      ))}
      <g transform={`rotate(${bank})`}>
        <path d="M-6 -1 L-19 11 L-19 15.5 L-6 12 Z" fill={h.trim} />
        <path d="M6 -1 L19 11 L19 15.5 L6 12 Z" fill={h.trim} />
        <path d="M-6 -1 L-19 11" stroke="#ffffff" strokeOpacity=".4" strokeWidth=".8" />
        <path d="M0 -23 C 5 -15, 7.5 -5, 7.5 7 L 6 16.5 L -6 16.5 L -7.5 7 C -7.5 -5, -5 -15, 0 -23 Z" fill={`url(#${id}v)`} stroke={h.dark} strokeWidth=".8" />
        <path d="M0 -12 L0 12" stroke={h.trim} strokeWidth="1.4" opacity=".8" />
        <ellipse cx="0" cy="-8" rx="3.4" ry="5.6" fill={`url(#${id}c)`} />
        <path d="M-1.6 -11.5 Q -1 -13 0.4 -13.2" stroke="#ffffff" strokeWidth=".9" fill="none" opacity=".9" />
        <circle cx="-17.5" cy="13" r="1.1" fill="#ff5a5a" />
        <circle cx="17.5" cy="13" r="1.1" fill="#6dff9a" />
      </g>
      {shield > 0 && <circle cx="0" cy="0" r="27" fill={h.flame} fillOpacity={0.12 * shield} stroke={h.flame} strokeWidth="1.6" opacity={shield} />}
    </g>
  );
}

/**
 * The ship from the front, standing on its legs: the feet at (±19, 0), the hull above them up to
 * y −37. `burn` 0–1 fires the two braking thrusters under it; `tilt` rocks it after a hard landing.
 */
export function Lander({ era, burn = 0, tilt = 0, shield = 0 }: { era: Era; burn?: number; tilt?: number; shield?: number }) {
  const id = useSvgId();
  const h = hullOf(era);
  return (
    <g transform={`rotate(${tilt} 0 -4)`}>
      <HullDefs id={id} h={h} />
      {[-12, 12].map((x) => (
        <g key={x}>
          <ellipse cx={x} cy={-4} rx={5 + 5 * burn} ry={4 + 9 * burn} fill={`url(#${id}g)`} opacity={0.35 + 0.65 * burn} />
          <g transform={`translate(${x} -8) scale(1 ${0.25 + 1.25 * burn})`}>
            <path d="M-3.2 0 Q -2.4 10 0 20 Q 2.4 10 3.2 0 Z" fill={`url(#${id}d)`} />
          </g>
        </g>
      ))}
      <path d="M-10 -13 L-19 0 M10 -13 L19 0" stroke={h.dark} strokeWidth="2.4" strokeLinecap="round" />
      <path d="M-10 -13 L-19 0 M10 -13 L19 0" stroke={h.light} strokeWidth=".8" strokeOpacity=".5" />
      <rect x="-22.5" y="-1.6" width="7" height="2.4" rx="1" fill={h.mid} />
      <rect x="15.5" y="-1.6" width="7" height="2.4" rx="1" fill={h.mid} />
      {[-12, 12].map((x) => (
        <g key={x}>
          <rect x={x - 3.4} y="-11.5" width="6.8" height="4" rx="1" fill={h.dark} />
          <ellipse cx={x} cy="-16" rx="5.6" ry="6.4" fill={`url(#${id}v)`} stroke={h.dark} strokeWidth=".7" />
        </g>
      ))}
      <path d="M-15 -18 C -15 -31, -8 -37, 0 -37 C 8 -37, 15 -31, 15 -18 L 12 -12 L -12 -12 Z" fill={`url(#${id}h)`} stroke={h.dark} strokeWidth=".8" />
      <path d="M-13.5 -16.5 L 13.5 -16.5" stroke={h.trim} strokeWidth="1.8" />
      <path d="M-10 -26 C -6 -31, 6 -31, 10 -26 L 9 -21.5 L -9 -21.5 Z" fill={`url(#${id}c)`} />
      <path d="M-6.5 -28 C -4 -29.6, 0 -30, 2 -29.8" stroke="#ffffff" strokeWidth=".9" fill="none" opacity=".85" />
      <path d="M-11 -30 C -7 -35, 5 -36, 9 -33" stroke="#ffffff" strokeWidth=".7" fill="none" opacity=".45" />
      <circle cx="-14.8" cy="-19" r="1.2" fill="#ff5a5a" />
      <circle cx="14.8" cy="-19" r="1.2" fill="#6dff9a" />
      {shield > 0 && <ellipse cx="0" cy="-17" rx="30" ry="25" fill={h.flame} fillOpacity={0.12 * shield} stroke={h.flame} strokeWidth="1.6" opacity={shield} />}
    </g>
  );
}

/** A fixed scatter of points, the same on every render, for skies and dust. */
export const SCATTER: readonly { x: number; y: number; r: number; d: number }[] = Array.from({ length: 24 }, (_, i) => ({
  x: ((i * 0.618034) % 1 + (i % 3) * 0.07) % 1,
  y: ((i * 0.381966 + 0.13) * 1.7) % 1,
  r: 0.6 + ((i * 7) % 5) * 0.28,
  d: (i * 0.37) % 3,
}));
